import _ from "lodash";

type ConfigLevel = Record<string, any>;

/**
 * Resolve a config field via the precedence chain:
 *   division > season > tournament
 *
 * Each argument is the `.config` object of the corresponding entity. Pass `{}`
 * (or omit) for any level that doesn't apply.
 *
 * The registration is NOT a tier (ADR 0034). It used to be the least-specific
 * fallback; existing registration-level values were migrated onto the parent
 * tournament/contest by `apps/api/api/scripts/24-unify-payment-into-config.ts`,
 * and the admin registration wizard mirrors its settings onto the parent
 * (useRegistrationDraft.saveParentConfig), which is the authoritative write.
 */
export default function getConfig({ division = {}, season = {}, tournament = {} }: {
  division?: ConfigLevel;
  season?: ConfigLevel;
  tournament?: ConfigLevel;
}): ConfigLevel {
  const getValueFromHierarchy = (path: string) => {
    const divisionValue = _.get(division, path);
    if (divisionValue !== undefined && divisionValue !== null) {
      return divisionValue;
    }

    const seasonValue = _.get(season, path);
    if (seasonValue !== undefined && seasonValue !== null) {
      return seasonValue;
    }

    const tournamentValue = _.get(tournament, path);
    if (tournamentValue !== undefined && tournamentValue !== null) {
      return tournamentValue;
    }

    return undefined;
  };

  const config: ConfigLevel = {};

  // Every field in ConfigSchema follows the same first-wins hierarchy:
  // division > league > tournament. The most-specific level with a
  // non-null/non-undefined value wins. No per-field exceptions.
  const fields = [
    'requirePlayers',
    'minPlayers',
    'maxPlayers',
    'requireCoaches',
    'minCoaches',
    'maxCoaches',
    'disableSync',
    'hideTeams',
    'hideSchedules',
    'hideStandings',
    'showStats',
    'showAllTeams',
    'excludeTeamName',
    'gameDurationInMinutes',
    'requireTeamRepresentatives',
    'minTeamRepresentatives',
    'maxTeamRepresentatives',
    'hasWaitlist',
    'autoPromote',
    'promotionPaymentWindowHours',
    'capacity',
    // 'tiebreakers' is NOT here — it resolves per-sport-key below.
    'breakPlayoffTiesBySeed',
    'gameStats',
    'statDisplayMode',
    // Payment/donation are ObjectId REFS resolved like any other config field
    // (ADR 0034 — they used to be top-level entity fields with hand-rolled
    // fallback chains). The resolved value may be a bare id or a populated
    // PaymentProduct object depending on the caller's fetch.
    'paymentProduct',
    'donationProduct',
  ];

  fields.forEach((field) => {
    const value = getValueFromHierarchy(field);
    if (value !== undefined) {
      config[field] = value;
    }
  });

  // Tiebreakers — sport-keyed arrays under `config.tiebreakers`, resolved PER
  // SPORT KEY (like eligibility below) so one sport's division override can't
  // shadow the parent's other sports. An EMPTY array means "not set" and falls
  // through to the parent: documents created before 2026-09-23 carry a stamped
  // `tiebreakers: { basketball: [], ... }` (the Mongoose implicit-array-default
  // bug — see apps/api/api/models/shared/index.ts and migration script 20), and
  // the admin modal used to write empty arrays when all rules were removed.
  // Trade-off (same as eligibility): a child cannot express "explicitly NO
  // tiebreakers" while a parent has a chain — removing all rules reverts to
  // the inherited order.
  const isMeaningfulTiebreakers = (v: unknown): boolean => Array.isArray(v) && v.length > 0;
  const tiebreakers: ConfigLevel = {};
  const sportKeys = new Set<string>();
  [division, season, tournament].forEach((level) =>
    Object.keys(_.get(level, 'tiebreakers') || {}).forEach((k) => sportKeys.add(k)));
  sportKeys.forEach((sportKey) => {
    for (const level of [division, season, tournament]) {
      const v = _.get(level, `tiebreakers.${sportKey}`);
      if (isMeaningfulTiebreakers(v)) {
        tiebreakers[sportKey] = v;
        return;
      }
    }
  });
  if (Object.keys(tiebreakers).length > 0) {
    config.tiebreakers = tiebreakers;
  }

  // Player eligibility — grouped under `config.eligibility` but resolved
  // per-sub-key so a division can override one constraint while inheriting the
  // others. Output shape matches the input:
  // `effectiveConfig.eligibility.{gender|age|rating}`. An EMPTY value (empty
  // array for gender, empty object for rating/age) is treated as "not set" so
  // it falls through to the parent instead of shadowing it — the admin Settings
  // form always emits these keys (often empty), so empty must mean "inherit".
  const isMeaningfulEligibility = (v: unknown): boolean => {
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return true;
  };
  const getEligibilityValue = (sub: string) => {
    const path = `eligibility.${sub}`;
    for (const level of [division, season, tournament]) {
      const v = _.get(level, path);
      if (isMeaningfulEligibility(v)) return v;
    }
    return undefined;
  };
  const eligibility: ConfigLevel = {};
  ['gender', 'age', 'rating'].forEach((sub) => {
    const value = getEligibilityValue(sub);
    if (value !== undefined) eligibility[sub] = value;
  });
  if (Object.keys(eligibility).length > 0) {
    config.eligibility = eligibility;
  }

  return config;
}
